/**
 * The sales deals slice of the data provider (DataProvider.deals): the deal board, a deal, its moves, trade-ins, notes
 * and tasks, the sales rotation, the store's deal settings and the manager view, over the deals routes (switch
 * crm_deals on the store; the API answers 404 feature_disabled while it, crm_core or dealer_workspace is off). Live
 * only: the fixture tenant is a workshop, so its slice answers "not switched on" the same way.
 *   /v1/app/deals*   /v1/app/crm/appointments (a visit set from a deal)
 */
import { APP, ApiError, type Http } from '@app/core/http';
import type { CrmMode, DealStage, Ratio } from './dealsModel';

export type UUID = string;
export type ISO = string;

export interface DealVehicle {
  condition: 'new' | 'used' | 'cpo' | null;
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  stock_number: string | null;
  vin: string | null;
  unit_id: UUID | null;
  in_stock: boolean | null;
  unit_status: string | null;
}
export interface Deal {
  id: UUID;
  deal_no: number;
  version: number;
  customer: { id: UUID; name: string };
  source: string;
  source_detail: string | null;
  stage: DealStage;
  stage_changed_at: ISO;
  milestones: Record<
    'first_contact_at' | 'appointment_set_at' | 'appointment_at' | 'showed_at' | 'test_drive_at' | 'working_at' | 'sold_at' | 'lost_at',
    ISO | null
  >;
  appointment_id: UUID | null;
  lost_reason: string | null;
  lost_note: string | null;
  dms_deal_ref: string | null;
  vehicle: DealVehicle;
  owner: { id: UUID; name: string | null } | null;
  assigned_at: ISO | null;
  assigned_by: string | null;
  setter: { id: UUID; name: string | null } | null;
  claim: { user_id: UUID; name: string | null; until: ISO } | null;
  previous_deal_id: UUID | null;
  in_crm: boolean;
  next_task_due_at: ISO | null;
  sources: number | null;
  actions: DealStage[];
  created_at: ISO;
  updated_at: ISO;
}
export interface Trade {
  id: UUID;
  year: number | null;
  make: string | null;
  model: string | null;
  trim: string | null;
  mileage: number | null;
  vin: string | null;
  owes_money: 'yes' | 'no' | 'unknown';
  condition_note: string | null;
  appraised: boolean;
  version: number;
}
export interface DealDetail extends Deal {
  mode: CrmMode;
  person: { id: UUID; name: string; phone_e164: string | null; email: string | null; preferred_language: string | null };
  trades: Trade[];
  calls: { id: UUID; source: string; kind: string; outcome: string; answered_at: ISO; lead_email_status: string | null }[];
  other_sources: { kind: string; at: ISO }[];
  history: {
    id: number;
    event: string;
    from_stage: string | null;
    to_stage: string | null;
    actor: string;
    actor_name: string | null;
    changes: Record<string, unknown>;
    created_at: ISO;
  }[];
}
export interface Board {
  items: Deal[];
  counts: Record<DealStage, number>;
  mode: CrmMode;
  rotation: { members: number; on_duty: number };
  unassigned: number;
  untouched: number;
}
export interface BoardQuery {
  view?: 'mine' | 'unassigned' | 'all';
  source?: string;
}
export interface DealSettings {
  crm_mode: CrmMode;
  escalate: boolean;
  respond_minutes: number;
  adf_in_enabled: boolean;
  lead_address: string | null;
  lead_address_ready: boolean;
  version: number;
}
export interface RotationMember {
  user_id: UUID;
  position: number;
  on_duty: boolean;
  receives: string[];
  name: string | null;
  role: string;
  last_assigned_at?: ISO | null;
}
export interface Rotation {
  members: RotationMember[];
  staff: { id: UUID; name: string | null; role: string }[];
  next_position: number;
  crm_mode: CrmMode;
}
export interface Rates {
  cohort: number;
  speed: { median_minutes: number | null; within_target: Ratio; untouched: number; contacted: Ratio };
  set_rate: Ratio;
  show_rate: Ratio;
  close_rate: Ratio | null;
  sold_of_showed: Ratio | null;
  lost_reasons: Record<string, number>;
  marked: Ratio;
}
export interface Report {
  from: ISO;
  to: ISO;
  mode: CrmMode;
  time_zone: string;
  hours_known: boolean;
  total: Rates;
  by_source: (Rates & { key: string | null })[];
  by_owner: (Rates & { key: string | null; name: string | null })[];
  by_setter: (Rates & { key: string | null; name: string | null })[];
}
export interface VehicleInput {
  condition?: 'new' | 'used' | 'cpo' | null;
  year?: number | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  stock_number?: string | null;
  vin?: string | null;
}
export interface PersonInput {
  name?: string;
  phone?: string;
  email?: string;
}
export interface TradeInput {
  year?: number | null;
  make?: string | null;
  model?: string | null;
  trim?: string | null;
  mileage?: number | null;
  vin?: string | null;
  owes_money?: 'yes' | 'no' | 'unknown';
  condition_note?: string | null;
  appraised?: boolean;
  version?: number;
}

export interface Api {
  board(q: BoardQuery): Promise<Board>;
  deal(id: UUID): Promise<DealDetail>;
  create(
    body: PersonInput & { source_detail?: string | null; vehicle?: VehicleInput; owner_user_id?: UUID | null },
    key: string,
  ): Promise<DealDetail>;
  walkIn(body: PersonInput & { vehicle?: VehicleInput; owner_user_id?: UUID | null; trade?: boolean }, key: string): Promise<DealDetail>;
  patch(
    id: UUID,
    body: { version: number; vehicle?: VehicleInput | null; source_detail?: string | null; dms_deal_ref?: string | null },
  ): Promise<DealDetail>;
  stage(
    id: UUID,
    body: { to: DealStage; lost_reason?: string; lost_note?: string | null; dms_deal_ref?: string | null },
  ): Promise<DealDetail>;
  act(id: UUID, action: 'take' | 'claim' | 'release'): Promise<DealDetail>;
  assign(id: UUID, userId: UUID): Promise<DealDetail>;
  saveTrade(id: UUID, tradeId: UUID | null, body: TradeInput): Promise<DealDetail>;
  deleteTrade(id: UUID, tradeId: UUID): Promise<DealDetail>;
  addNote(id: UUID, body: string): Promise<{ activity_id: UUID }>;
  addTask(id: UUID, body: { title: string; due_at?: ISO | null; priority?: 'normal' | 'high' }): Promise<{ task_id: UUID }>;
  requestVisit(body: {
    customer_id: UUID;
    starts_at: ISO | null;
    requested_time_text?: string | null;
    interest_stock_number?: string | null;
  }): Promise<unknown>;
  settings(): Promise<DealSettings>;
  saveSettings(
    body: Partial<Pick<DealSettings, 'crm_mode' | 'escalate' | 'respond_minutes' | 'adf_in_enabled'>> & { version?: number },
  ): Promise<DealSettings>;
  rotation(): Promise<Rotation>;
  saveRotation(members: { user_id: UUID; on_duty: boolean; receives?: string[] }[]): Promise<Rotation & { assigned: number }>;
  report(q: { from: ISO; to: ISO }): Promise<Report>;
}

const D = `${APP}/deals`;
const e = encodeURIComponent;
const one = (r: { deal: DealDetail }) => r.deal;

export const live = (http: Http): Api => ({
  board: (q) => http.get(`${D}/board`, { query: { view: q.view, source: q.source } }),
  deal: (id) => http.get<{ deal: DealDetail }>(`${D}/${e(id)}`).then(one),
  create: (body, key) => http.post<{ deal: DealDetail }>(D, body, { idempotencyKey: key }).then(one),
  walkIn: (body, key) => http.post<{ deal: DealDetail }>(`${D}/walk-in`, body, { idempotencyKey: key }).then(one),
  patch: (id, body) => http.patch<{ deal: DealDetail }>(`${D}/${e(id)}`, body).then(one),
  stage: (id, body) => http.post<{ deal: DealDetail }>(`${D}/${e(id)}/stage`, body).then(one),
  act: (id, action) => http.post<{ deal: DealDetail }>(`${D}/${e(id)}/${action}`, {}).then(one),
  assign: (id, userId) => http.post<{ deal: DealDetail }>(`${D}/${e(id)}/assign`, { user_id: userId }).then(one),
  saveTrade: (id, tradeId, body) =>
    http.put<{ deal: DealDetail }>(`${D}/${e(id)}/trades${tradeId ? `/${e(tradeId)}` : ''}`, body).then(one),
  deleteTrade: (id, tradeId) => http.del<{ deal: DealDetail }>(`${D}/${e(id)}/trades/${e(tradeId)}`).then(one),
  addNote: (id, body) => http.post(`${D}/${e(id)}/notes`, { body }),
  addTask: (id, body) => http.post(`${D}/${e(id)}/tasks`, body),
  requestVisit: (body) => http.post(`${APP}/crm/appointments`, { kind: 'test_drive', ...body }),
  settings: () => http.get<{ settings: DealSettings }>(`${D}/settings`).then((r) => r.settings),
  saveSettings: (body) => http.put<{ settings: DealSettings }>(`${D}/settings`, body).then((r) => r.settings),
  rotation: () => http.get(`${D}/rotation`),
  saveRotation: (members) => http.put(`${D}/rotation`, { members }),
  report: (q) => http.get<{ report: Report }>(`${D}/report`, { query: { from: q.from, to: q.to } }).then((r) => r.report),
});

/** The fixture tenant is a workshop: every deals call answers as the API does while the switch is off. */
export const fixture = (): Api => {
  const off = () => Promise.reject(new ApiError(404, 'feature_disabled', null, { ok: false, error: 'feature_disabled' }));
  return new Proxy({} as Api, { get: () => off });
};
